/* eslint-disable no-nested-ternary */
import { enqueueSnackbar } from 'notistack';
import { useQueryClient } from 'react-query';
import { useState, useCallback, useMemo } from 'react';

import {
  Box,
  Tabs,
  Tab,
  Dialog,
  Button,
  Checkbox,
  FormGroup,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  FormControlLabel,
} from '@mui/material';

import { DetailsFavouritesCollectionModel, FavouritesDataModel } from 'src/api/models/favourites';
import {
  useAddProductToFavouritesCollection,
  useDeleteFavouritesItem,
} from 'src/api/hooks/api-hooks';

import CreateSharedFavouritesCollectionForm from './create-shared-favourties-collection-dialog';
import CreatePersonalFavouritesCollectionForm from './create-personal-favourites-collection-form';

interface DialogProps {
  open: boolean;
  onClose: any;
  favouritesCollectionsData: FavouritesDataModel;
  isFavouritesLoading?: boolean;
  favouritesCollectionsError?: unknown;
  productStockId: string;
  preSelectedFavourites?: string[];
}

export default function AddProductToFavouritesCateringManagerDialog({
  open,
  onClose,
  favouritesCollectionsData,
  isFavouritesLoading,
  favouritesCollectionsError,
  productStockId,
  preSelectedFavourites = [],
}: DialogProps) {
  const { mutate: addProductToFavouritesCollection } = useAddProductToFavouritesCollection();
  const { mutate: deleteFavouritesItem } = useDeleteFavouritesItem();
  const queryClient = useQueryClient();
  const dialogTitleId = 'favourites-dialog-title';
  const dialogDescriptionId = 'favourites-dialog-description';

  const [selectedCollectionIds, setSelectedCollectionIds] = useState<string[]>(() => {
    if (preSelectedFavourites.length > 0) {
      return preSelectedFavourites;
    }
    // Initialize with collections that already contain this product
    const allCollections = [
      ...(favouritesCollectionsData.personal || []),
      ...(favouritesCollectionsData.orgShared || []),
      ...(favouritesCollectionsData.siteShared || []),
    ];
    return (
      allCollections
        .filter((c) => c.items.some((item) => item.productStockId === productStockId))
        .map((c) => c.id) || []
    );
  });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showCreateSharedForm, setShowCreateSharedForm] = useState(false);
  const [currentTab, setCurrentTab] = useState<'personal' | 'shared'>('personal');

  const displayedCollections = useMemo(() => {
    if (currentTab === 'personal') {
      return favouritesCollectionsData.personal || [];
    }
    return [
      ...(favouritesCollectionsData.orgShared || []),
      ...(favouritesCollectionsData.siteShared || []),
    ];
  }, [favouritesCollectionsData, currentTab]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: 'personal' | 'shared') => {
    setCurrentTab(newValue);
  };

  const handleCollectionToggle = (collectionId: string) => {
    setSelectedCollectionIds((prevSelected) =>
      prevSelected.includes(collectionId)
        ? prevSelected.filter((id) => id !== collectionId)
        : [...prevSelected, collectionId]
    );
  };

  const handleAddToCollection = () => {
    const collectionsToAdd = selectedCollectionIds.filter((collectionId) => {
      const collection = displayedCollections.find((c) => c.id === collectionId);
      return (
        collection &&
        !collection.items.some((item) => item.productStockId === productStockId)
      );
    });

    const collectionsToRemove = displayedCollections.filter((collection) => {
      const isInCollection = collection.items.some(
        (item) => item.productStockId === productStockId
      );
      const isSelected = selectedCollectionIds.includes(collection.id);
      return isInCollection && !isSelected;
    });

    if (collectionsToAdd.length === 0 && collectionsToRemove.length === 0) {
      enqueueSnackbar('No changes to save', {
        variant: 'info',
      });
      onClose();
      return;
    }

    collectionsToAdd.forEach((collectionId) => {
      const collection = displayedCollections.find((c) => c.id === collectionId);
      if (collection) {
        addProductToFavouritesCollection(
          {
            pathParams: {
              collectionId,
            },
            body: {
              productStockId,
            },
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries('favourites');
              queryClient.invalidateQueries('products');
              queryClient.invalidateQueries([
                'productDetails',
                { stockId: productStockId },
              ]);
              enqueueSnackbar(`Product added to '${collection.name}' successfully!`, {
                variant: 'success',
              });
            },
          }
        );
      }
    });

    // Remove from unselected collections
    collectionsToRemove.forEach((collection) => {
      const item = collection.items.find((i) => i.productStockId === productStockId);
      if (item) {
        deleteFavouritesItem(
          {
            pathParams: {
              collectionId: collection.id,
              itemId: item.id,
            },
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries('favourites');
              queryClient.invalidateQueries('products');
              queryClient.invalidateQueries([
                'productDetails',
                { stockId: productStockId },
              ]);
              enqueueSnackbar(`Product removed from '${collection.name}' successfully!`, {
                variant: 'success',
              });
            },
          }
        );
      }
    });

    onClose();
  };

  const isSubmitDisabled = useCallback(() => {
    const hasCollectionsToAdd = selectedCollectionIds.some((collectionId) => {
      const collection = displayedCollections.find((c) => c.id === collectionId);
      return (
        collection &&
        !collection.items.some((item) => item.productStockId === productStockId)
      );
    });

    const hasCollectionsToRemove = displayedCollections.some((collection) => {
      const isInCollection = collection.items.some(
        (item) => item.productStockId === productStockId
      );
      const isSelected = selectedCollectionIds.includes(collection.id);
      return isInCollection && !isSelected;
    });

    return !hasCollectionsToAdd && !hasCollectionsToRemove;
  }, [selectedCollectionIds, displayedCollections, productStockId]);

  const handlePersonalFormSuccess = (collectionId: string) => {
    setSelectedCollectionIds((prev) => [...prev, collectionId]);
  };

  if (isFavouritesLoading)
    return (
      <Dialog open={open} onClose={onClose} aria-labelledby={dialogTitleId}>
        <CircularProgress aria-label="Loading Favourites Lists" />
      </Dialog>
    );

  if (favouritesCollectionsData && !favouritesCollectionsError)
    return (
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby={dialogTitleId}
        aria-describedby={dialogDescriptionId}
        aria-modal
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          onEntering: () => {
            const titleElement = document.getElementById(dialogTitleId);
            if (titleElement) {
              titleElement.setAttribute('tabIndex', '-1');
              titleElement.focus();
            }
          },
        }}
      >
        {!showCreateForm && !showCreateSharedForm ? (
          <>
            <DialogTitle id={dialogTitleId}>Edit Favourites</DialogTitle>
            <DialogContent>
              <Tabs value={currentTab} onChange={handleTabChange} sx={{ mb: 2 }}>
                <Tab label="Personal" value="personal" />
                <Tab label="Shared" value="shared" />
              </Tabs>

              <Typography id={dialogDescriptionId} sx={{ mb: 2 }}>
                Select lists to add this product to:
              </Typography>

              {displayedCollections.length > 0 && (
                <FormGroup sx={{ ml: 3 }}>
                  {displayedCollections.map((collection: DetailsFavouritesCollectionModel) => {
                    const isChecked = selectedCollectionIds.includes(collection.id);
                    return (
                      <FormControlLabel
                        key={collection.id}
                        control={
                          <Checkbox
                            checked={isChecked}
                            onChange={() => handleCollectionToggle(collection.id)}
                            aria-label={`${isChecked ? 'Remove from' : 'Add to'} ${collection.name}`}
                          />
                        }
                        label={collection.name}
                      />
                    );
                  })}
                </FormGroup>
              )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                onClick={() => {
                  if (currentTab === 'personal') {
                    setShowCreateForm(true);
                  } else {
                    setShowCreateSharedForm(true);
                  }
                }}
              >
                + Create New List
              </Button>
              <Box sx={{ display: 'flex', gap: 1, ml: currentTab === 'shared' ? 'auto' : 0 }}>
                <Button
                  aria-label="Cancel adding to Favourites List"
                  variant="outlined"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleAddToCollection}
                  disabled={isSubmitDisabled()}
                >
                  Save Changes
                </Button>
              </Box>
            </DialogActions>
          </>
        ) : showCreateForm ? (
          <CreatePersonalFavouritesCollectionForm
            onBack={() => setShowCreateForm(false)}
            onSuccess={handlePersonalFormSuccess}
          />
        ) : (
          <CreateSharedFavouritesCollectionForm onBack={() => setShowCreateSharedForm(false)} />
        )}
      </Dialog>
    );

  return null;
}
